import type {
  ActionIconProps as MantineActionIconProps,
  TooltipProps} from '@mantine/core';
import {
  ActionIcon as MantineActionIcon,
  Tooltip
} from '@mantine/core';
import type {MouseEventHandler, ReactNode} from 'react';

/**
 * Icon-only button with a required tooltip/aria-label, wrapped in a Mantine
 * `Tooltip`. Defaults to `variant="subtle"`, `size="sm"`.
 * @registry Icon-only action button with built-in tooltip + accessible name.
 *
 * Ported from `library-m/components/action-icons/DefaultActionIcon`. Prop
 * surface kept identical: `tooltipText` is required (also doubles as the
 * `aria-label`), `tooltipProps` optionally overrides the wrapping Tooltip.
 */
export interface ActionIconProps extends MantineActionIconProps {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  tooltipText: string;
  tooltipProps?: Partial<Omit<TooltipProps, 'children' | 'label'>>;
  children?: ReactNode;
}

export const ActionIcon = ({
  variant = 'subtle',
  size = 'sm',
  children,
  tooltipText,
  tooltipProps,
  ...props
}: ActionIconProps) => (
  <Tooltip label={tooltipText} {...tooltipProps}>
    <MantineActionIcon
      variant={variant}
      size={size}
      {...props}
      aria-label={tooltipText}>
      {children}
    </MantineActionIcon>
  </Tooltip>
);
